/**
 * Coordinates the first-visit job-seeker onboarding flow (intent capture +
 * resume import) with the product tour so the two never overlap.
 *
 * The onboarding flow always runs first. When it finishes — resume imported,
 * resume skipped, or the intent step dismissed — it calls
 * {@link completeOnboardingAndStartTour}, which marks the intent step done and
 * fires {@link PRODUCT_TOUR_START_EVENT}. The ProductTour listens for that
 * event and only then begins the walkthrough.
 */

/** Window event the onboarding flow fires to kick off the product tour. */
export const PRODUCT_TOUR_START_EVENT = 'rc:start-product-tour';

/** Persisted flag: the seeker has been through the first-visit intent step. */
const INTENT_DONE_KEY = 'jsd-intent-capture-done';

/** Persisted primary goal the seeker picked, kept for later personalisation. */
const USER_INTENT_KEY = 'jsd-user-intent';

/** True once the seeker has completed (or skipped) the first-visit intent step. */
export function hasCompletedOnboardingIntent(): boolean {
  try {
    return localStorage.getItem(INTENT_DONE_KEY) === '1';
  } catch {
    // Private mode / storage blocked — treat as "seen" so we never trap the user
    // in a loop where the onboarding keeps reappearing.
    return true;
  }
}

/**
 * Marks the onboarding intent step complete (persisting the chosen goal) and
 * signals the ProductTour to start. Safe to call from anywhere in the flow and
 * safe to call more than once.
 */
export function completeOnboardingAndStartTour(intent?: string): void {
  try {
    localStorage.setItem(INTENT_DONE_KEY, '1');
    if (intent) localStorage.setItem(USER_INTENT_KEY, intent);
  } catch {
    /* ignore persistence failures (private mode) */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_START_EVENT));
  }
}
